import { btn, field } from '../common/AdminUI';
import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { UploadCloud, FileText, X, Loader2, Send } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const MAX_SIZE_BYTES = 15 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatSize = (bytes) => (bytes >= 1024 * 1024
  ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const isPdf = (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);

// Hand-feeds one or more LEO PDFs into the ingestion pipeline (same one the
// Gmail automation uses), so manually received documents land in the
// Pending Approval queue like emailed ones. Super-admin only; authenticated
// with the normal session JWT.
const ManualLeoUpload = ({ onQueued }) => {
  const { getToken } = useAuth();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const rejected = [];
    const accepted = [];
    incoming.forEach((f) => {
      if (!isPdf(f)) rejected.push(`${f.name}: not a PDF`);
      else if (f.size > MAX_SIZE_BYTES) rejected.push(`${f.name}: over 15 MB`);
      else accepted.push(f);
    });
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...accepted.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
    setErrors((e) => ({ ...e, files: rejected.join(' · '), api: '' }));
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const next = {};
    if (!files.length) next.files = 'Select at least one LEO PDF';
    const email = recipientEmail.trim().toLowerCase();
    if (!email) next.recipientEmail = 'Recipient email is required';
    else if (!EMAIL_RE.test(email)) next.recipientEmail = 'Enter a valid email address';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    const token = getToken?.();
    if (!token) {
      setErrors({ api: 'Your session has expired. Please log in again.' });
      return;
    }

    setSubmitting(true);
    setErrors({});
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    form.append('recipientEmail', recipientEmail.trim().toLowerCase());

    try {
      const res = await fetch(`${API_BASE_URL}/automation/manual-ingest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      console.log('Manual LEO ingest response:', res.status, data);

      if (!res.ok || data?.success === false) {
        setErrors({ api: data?.message || `Upload failed (HTTP ${res.status})` });
        return;
      }

      const n = files.length;
      toast.success(`${n} LEO PDF${n > 1 ? 's' : ''} queued — they will show up here once processing finishes.`);
      setFiles([]);
      setRecipientEmail('');
      onQueued?.(data);
    } catch (err) {
      console.error('Manual LEO ingest error:', err);
      setErrors({ api: 'Unable to reach the server. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const totalSize = files.reduce((s, f) => s + f.size, 0);

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-5 rounded-2xl border border-slate-200 bg-white shadow-sm p-4 sm:p-5
                 dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none"
    >
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300 grid place-items-center flex-shrink-0">
          <UploadCloud size={18} />
        </span>
        <div>
          <h4 className="text-[13.5px] font-bold text-slate-900 dark:text-white leading-tight">Manual LEO upload</h4>
          <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">
            Upload one or more LEO PDFs by hand — they run through the same automation as emailed documents and land in this queue.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_auto] gap-3 lg:gap-4 items-start">
        {/* File drop zone + selected list */}
        <div>
          <label className="flex items-baseline gap-1.5 mb-1.5">
            <span className="text-[12px] font-bold text-slate-700 dark:text-slate-300">LEO PDF(s)</span>
            <span className="text-rose-500">*</span>
          </label>
          <label
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
            className={`flex items-center justify-center gap-2 h-11 px-3 rounded-xl border border-dashed cursor-pointer
                         text-[13px] font-semibold transition ${
              errors.files
                ? 'border-rose-400 text-rose-500 bg-rose-50/60 dark:bg-rose-500/10'
                : dragging
                  ? 'border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300'
                  : 'border-slate-300 bg-slate-50 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/60 ' +
                    'dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400 dark:hover:border-blue-500/60 dark:hover:text-blue-300'
            }`}
          >
            <UploadCloud size={18} />
            <span>{files.length ? 'Add more PDFs' : 'Click to choose or drop PDFs here'}</span>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
          </label>

          {files.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {files.map((f, idx) => (
                <li
                  key={`${f.name}:${f.size}`}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50
                             dark:border-slate-800 dark:bg-slate-900/50"
                >
                  <FileText size={16} className="text-blue-600 dark:text-blue-300 flex-shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-sm text-gray-800 dark:text-gray-100" title={f.name}>
                    {f.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{formatSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    disabled={submitting}
                    className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-800 text-gray-500 dark:text-gray-300 transition"
                    aria-label={`Remove ${f.name}`}
                  >
                    <X size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {errors.files
            ? <p className="mt-1 text-xs text-red-500">{errors.files}</p>
            : files.length > 0 && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {files.length} file{files.length > 1 ? 's' : ''} · {formatSize(totalSize)}
              </p>
            )}
        </div>

        {/* Recipient email */}
        <div>
          <label className="flex items-baseline gap-1.5 mb-1.5">
            <span className="text-[12px] font-bold text-slate-700 dark:text-slate-300">Recipient email</span>
            <span className="text-rose-500">*</span>
          </label>
          <input
            type="email"
            value={recipientEmail}
            onChange={(e) => { setRecipientEmail(e.target.value); setErrors((er) => ({ ...er, recipientEmail: '', api: '' })); }}
            placeholder="admin-inbox@example.com"
            disabled={submitting}
            className={`${field} ${errors.recipientEmail ? 'border-rose-400' : ''}`}
          />
          {errors.recipientEmail
            ? <p className="mt-1.5 text-[11.5px] font-semibold text-rose-500">{errors.recipientEmail}</p>
            : <p className="mt-1.5 text-[11.5px] text-slate-500 dark:text-slate-400">The admin's fetch-PDF inbox these documents belong to.</p>}
        </div>

        {/* Submit */}
        <div className="lg:pt-[26px]">
          <button
            type="submit"
            disabled={submitting}
            className={`${btn.primary} w-full lg:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {submitting ? 'Uploading…' : 'Submit'}
          </button>
        </div>
      </div>

      {errors.api && (
        <div className="mt-3 rounded-xl px-4 py-2.5 text-[13px] font-semibold
                        border border-rose-200 bg-rose-50/70 text-rose-600
                        dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
          {errors.api}
        </div>
      )}
    </form>
  );
};

export default ManualLeoUpload;
